import { AccountView } from "../../../../models/view/account/account.view";
import { BasiqIoTransactionType } from "../../../../models/types/basiq-io-transaction.types";
import { CategoryView } from "../../../../models/view/category/category.view";
import { TransactionView } from "../../../../models/view/transaction/transaction.view";
import { TransactionStoreModel } from "../../../../models/store/transaction.store.model";
import { TransactionStatusEnum } from "../../../../models/enum/transactionType";
import { TransactionDirection } from "../../../../models/enum/transactionDirection";
import { Injectable } from "@angular/core";
import { SourceTransactionsView } from "@bitwarden/web-vault/app/models/view/source-transactions/source-transactions.view";
import { SourceTransactionStoreModel } from "@bitwarden/web-vault/app/models/store/source-transaction.store.model";
import { GlossDateFactory } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date.factory";
import { AllocationUtils } from "@bitwarden/web-vault/app/models/store/allocations/allocation.utils";

@Injectable({
  providedIn: "root",
})
export class BasiqTransactionMapper {
  toTransactionView(
    accountView: AccountView,
    basiqTransaction: BasiqIoTransactionType,
    categories: CategoryView[],
  ): TransactionView {
    const transactionStoreModel: TransactionStoreModel = this.toStoreModel(
      accountView,
      basiqTransaction,
      categories,
    );

    return new TransactionView(transactionStoreModel);
  }

  toSourceTransactionView(
    accountView: AccountView,
    transactionView: TransactionView,
    categoryNames: string[],
  ): SourceTransactionsView {
    const transactionStoreModel: SourceTransactionStoreModel = this.toSourceStoreModel(
      accountView,
      transactionView,
      categoryNames,
    );
    return new SourceTransactionsView(transactionStoreModel);
  }

  private toStoreModel(
    accountView: AccountView,
    basiqTransaction: BasiqIoTransactionType,
    categories: CategoryView[],
  ): TransactionStoreModel {
    const postDateISO =
      basiqTransaction.postDate || basiqTransaction.transactionDate || new Date().toISOString();
    const transactionDateISO = basiqTransaction.transactionDate || postDateISO;
    const timeZone = basiqTransaction?.timezone ? basiqTransaction.timezone : null;
    return {
      acId: accountView.id,
      ori: "basiq",
      bIB: undefined,
      bIBO: undefined,
      alc: AllocationUtils.createAllocationsFromCategoriesAndClassifications(
        categories,
        [],
        Number(basiqTransaction.amount),
      ).map((a) => a.toAllocationAsObjectType()),
      dc: new Date().toISOString(),
      dfn: TransactionStatusEnum.transaction,
      dir:
        basiqTransaction.direction === "credit"
          ? TransactionDirection.In
          : TransactionDirection.Out,
      dm: new Date().toISOString(),
      dsc: basiqTransaction.description,
      id: basiqTransaction.id,
      knd: basiqTransaction.type,
      lTid: [],
      xLid: [],
      sts: basiqTransaction.status,
      qty: {
        qty: {
          amt: Math.abs(Number(basiqTransaction.amount)),
          prcs: 8,
          sym: accountView.currency,
        },
        cvrt: 1,
        cvsym: "",
        ccy: accountView.currency,
      },
      srId: null,
      tDt: transactionDateISO
        ? GlossDateFactory.createFromISOString(
            transactionDateISO,
            false,
            timeZone,
          ).toGlossDateAsObject()
        : null,
      pDt: GlossDateFactory.createFromISOString(postDateISO, false, timeZone).toGlossDateAsObject(),
      v: 9,
      vid: "",
      vlPc: 0,
    };
  }

  private toSourceStoreModel(
    accountView: AccountView,
    transaction: TransactionView,
    categoryNames: string[],
  ): SourceTransactionStoreModel {
    return {
      dc: new Date().toISOString(),
      dm: null,
      id: crypto.randomUUID(),
      v: 2,
      vid: "",
      rw: "",
      acId: transaction.accountLink.id,
      sym: accountView.currency,
      ccy: accountView.currency,
      dsc: transaction.description,
      knd: transaction.kind,
      dir: transaction.direction,
      bIB: transaction.bankImportedBalance,
      qty: transaction.quantity.actualQuantity.amount.toString(),
      prc: null,
      cts: categoryNames,
      cls: [],
      srId: transaction.id,
      dfn: transaction.definition,
      tDt: transaction.postDate.toISOString(),
    };
  }
}
